/** What the object brush can place, grouped so a list of 355 is navigable.
 *
 * The palette is derived from the content registry rather than written down:
 * a map object the game ships and the editor cannot place is a gap that would
 * open silently every time somebody adds a def, and there are 355 of them.
 *
 * Two things are filtered out and both would be traps. A **town** object is
 * placed by putting a player's capital down, not by the object brush - a
 * `town_sunspire` sprite with no `TownState` behind it is scenery that looks like
 * a town. A **hero** object is the same story.
 */

import type { ContentRegistry, MapObjectDef } from "@game/core";
import { titleCase } from "../screens/format.js";

export interface PaletteEntry {
  readonly def: string;
  readonly kind: string;
  readonly name: string;
  readonly art: string;
  /** Cells the object covers; the editor draws a footprint hint from it. */
  readonly footprint: number;
  /** Can be flagged to a player, so the palette offers an owner. */
  readonly flaggable: boolean;
}

export interface PaletteGroup {
  readonly kind: string;
  readonly label: string;
  readonly entries: readonly PaletteEntry[];
}

/** Placed by the player tools, not by the object brush. */
const NOT_IN_PALETTE = new Set(["town", "hero"]);

/** Object kinds that carry an owner in `MapObjectState`. */
const FLAGGABLE = new Set(["mine", "dwelling", "garrison"]);

const GROUP_LABEL: Readonly<Record<string, string>> = Object.freeze({
  resource: "Resources",
  mine: "Mines",
  dwelling: "Dwellings",
  artifact: "Artifacts",
  monster: "Wandering monsters",
  treasure: "Treasure",
  scholar: "Scholars",
  shrine: "Shrines",
  boat: "Boats",
  garrison: "Garrisons",
  portal: "Portals and gates",
  bank: "Creature banks",
  bonus: "Visitable buildings",
  obstacle: "Scenery",
  sign: "Signs",
  keymaster: "Keymasters",
  gate: "Border gates",
});

const NAME_PREFIX = /^(?:obj)[._]|^(?:bld|bank|mine|dwl|mon|res|pu|tp|obs|boat)[._]/;

export function paletteName(def: MapObjectDef): string {
  let id = def.id;
  for (;;) {
    const next = id.replace(NAME_PREFIX, "");
    if (next === id) break;
    id = next;
  }
  return titleCase(id.replace(/[._]obj$/, "").replace(/[._]/g, " "));
}

export function buildPalette(content: ContentRegistry): readonly PaletteGroup[] {
  const byKind = new Map<string, PaletteEntry[]>();
  const ids = [...content.mapObjects.keys()].sort();
  for (const id of ids) {
    const def = content.mapObjects.get(id);
    if (def === undefined || NOT_IN_PALETTE.has(def.kind)) continue;
    const entry: PaletteEntry = {
      def: def.id,
      kind: def.kind,
      name: paletteName(def),
      art: def.art,
      footprint: def.footprint.length,
      flaggable: FLAGGABLE.has(def.kind),
    };
    const bucket = byKind.get(def.kind);
    if (bucket === undefined) byKind.set(def.kind, [entry]); else bucket.push(entry);
  }

  const groups: PaletteGroup[] = [];
  for (const kind of [...byKind.keys()].sort()) {
    const entries = (byKind.get(kind) ?? []).slice()
      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    groups.push({ kind, label: GROUP_LABEL[kind] ?? titleCase(kind), entries });
  }
  return groups;
}

/** Case-insensitive substring, over the name and the def id both: an author
 *  who knows the id should not have to know the label. */
export function filterPalette(
  groups: readonly PaletteGroup[], query: string,
): readonly PaletteGroup[] {
  const q = query.trim().toLowerCase();
  if (q === "") return groups;
  const out: PaletteGroup[] = [];
  for (const g of groups) {
    const entries = g.entries.filter(
      (e) => e.name.toLowerCase().includes(q) || e.def.toLowerCase().includes(q),
    );
    if (entries.length > 0) out.push({ ...g, entries });
  }
  return out;
}

export const paletteCount = (groups: readonly PaletteGroup[]): number =>
  groups.reduce((n, g) => n + g.entries.length, 0);
